import {
  PullRequestDetails,
  PullRequestDocument,
  GitHubReview,
  TestExecution,
  isRelevantStatusCheck,
  MAIN_BRANCH,
  ORCHESTRATOR_DISPATCH_CHECK,
} from '@finvari/deliver';
import {
  PARKED_LABELS,
  PRIORITY_LABEL,
  TEST_LANES,
  TestLaneDef,
  LaneStatus,
  UnitStatus,
  ReviewStage,
  ParkedReason,
  NODE,
  laneNodeIds,
} from './workflowConfig';

// The workflow position of a single PR. A PR occupies at most one review-lane node — `open`,
// `reviewing`, or `changes-requested`, or the parked node standing in for it — but an approved PR
// occupies none, since approval is a green state represented by absence (it leaves the review row
// for Ready), exactly like a passing test lane. It additionally occupies `merge-conflicts` whenever
// it is conflicted (a PR can be conflicted while still under review), plus one node in each test
// lane that is not green — see `nodeIds`. A parked PR is the exception: it occupies only its parked
// node (plus any scoped-lane run someone started by hand). `ready` and `merged` are exclusive
// terminal states.
export interface PrWorkflow {
  prNumber: number;
  parked: ParkedReason | null;
  review: ReviewStage;
  unit: UnitStatus;
  lanes: Record<string, LaneStatus>; // laneId -> status
  ready: boolean;
  merged: boolean;
  priority: boolean;
  nodeIds: string[];   // every workflow node this PR currently sits in
  blockers: string[];  // human-readable reasons the PR is not Ready (empty when ready/merged)
}

// The PR shape the derivation reads. The web contexts hand us PullRequestDetails objects that also
// carry the document-only fields at runtime (prStatus, testFreshness, testWaivers), so we widen the
// input to make those optionally available without forcing callers to have a full RefreshableItem.
export type WorkflowPr = PullRequestDetails &
  Partial<Pick<PullRequestDocument, 'prNumber' | 'prStatus' | 'testFreshness' | 'testWaivers'>>;

const FAILING_CONCLUSIONS = new Set(['failure', 'cancelled', 'timed_out', 'action_required']);

function labelNames(pr: WorkflowPr): Set<string> {
  return new Set((pr.pr?.labels ?? []).map(l => l.name));
}

function computeParked(pr: WorkflowPr): ParkedReason | null {
  const base = pr.pr?.base?.ref;
  // A PR that doesn't target main waits on its upstream so merge order stays correct.
  if (base && base !== MAIN_BRANCH) return 'upstream-wait';
  const labels = labelNames(pr);
  if (labels.has(PARKED_LABELS.stuck)) return 'stuck';
  if (labels.has(PARKED_LABELS.paused)) return 'paused';
  if (pr.pr?.draft) return 'draft';
  if (labels.has(PARKED_LABELS.wip)) return 'wip';
  return null;
}

function computeReview(pr: WorkflowPr): ReviewStage {
  const author = pr.pr?.user?.login;
  const reviews = pr.reviews ?? [];

  // Latest decisive state per non-author reviewer. COMMENTED/PENDING never override a prior
  // decision (same rule as the backend's getApprovers), so a comment after an approval keeps the
  // approval.
  const decisions = new Map<string, GitHubReview>();
  let sawComment = false;
  for (const review of reviews) {
    const login = review.user?.login;
    if (!login || login === author) continue;
    if (review.state === 'COMMENTED' || review.state === 'DISMISSED') {
      sawComment = true;
    }
    // COMMENTED never establishes a decision (it can't override a prior approval/changes-requested).
    if (review.state === 'COMMENTED') continue;
    const existing = decisions.get(login);
    if (!existing || new Date(review.submitted_at) >= new Date(existing.submitted_at)) {
      decisions.set(login, review);
    }
  }

  // A plain top-level PR comment (not a formal review) by anyone other than the author also counts
  // as engagement. It isn't a mechanical gate — it's a team-dynamics signal that a second person is
  // aware of the PR enough to have written on it, which is the transition out of "only the author has
  // touched this" that Reviewing represents. Bot/self comments are filtered out at ingestion, so any
  // comment left here whose author isn't the PR author is real engagement.
  const sawPlainComment = (pr.comments ?? []).some(c => c.user?.login && c.user.login !== author);

  const states = Array.from(decisions.values()).map(r => r.state);
  if (states.includes('CHANGES_REQUESTED')) return 'changes-requested';
  if (states.includes('APPROVED')) return 'approved';
  // A dismissed decision is activity even though it isn't approval/changes.
  if (sawComment || sawPlainComment || states.length > 0) return 'reviewing';
  return 'open';
}

function computeUnit(pr: WorkflowPr): UnitStatus {
  const statuses = pr.statuses ?? [];
  // GitHub has registered no check runs at all for this head, so nothing is running. Reading this as
  // green would let a freshly-pushed PR flash into Ready until the jobs register, so it is a queue
  // state instead. It is also where a PR GitHub is declining to test sits: GitHub skips pull_request
  // workflows entirely on a PR with merge conflicts, and queues nothing when our Actions minutes are
  // exhausted — in both cases no job ever appears, so "running" would be a lie the PR never escapes.
  if (statuses.length === 0) return 'needed';

  // The Orchestrator's `dispatch` job is the first check GitHub registers on every PR, and the unit
  // suites are registered by it. So while it exists but hasn't completed, jobs are still being set
  // up and zero relevant checks means "not yet", not "none". A head that predates the Orchestrator
  // has no `dispatch` at all but does have directly-registered checks, so it falls through and those
  // checks are judged on their own rather than waiting forever for a sentinel that will never come.
  const dispatch = statuses.find(c => c.name === ORCHESTRATOR_DISPATCH_CHECK);
  if (dispatch && dispatch.status !== 'completed') return 'running';

  const checks = statuses.filter(isRelevantStatusCheck);
  // Registration has settled and no relevant checks exist: a genuine docs-only PR that triggers no
  // unit suites. That is legitimately not-applicable rather than pending.
  if (checks.length === 0) return 'na';
  if (checks.some(c => FAILING_CONCLUSIONS.has(c.conclusion))) return 'failed';
  const pending = checks.some(
    c => c.status === 'queued' || c.status === 'in_progress' ||
      (c.status === 'completed' && (c.conclusion === null || c.conclusion === undefined))
  );
  if (pending) return 'running';
  return 'passed';
}

// True when any changed file makes this lane's scoped test required.
export function laneRequired(lane: TestLaneDef, changedFiles: string[]): boolean {
  return changedFiles.some(f => lane.requiredWhen.some(re => re.test(f)));
}

function computeLane(
  lane: TestLaneDef,
  pr: WorkflowPr,
  executions: TestExecution[],
  eligible: boolean
): LaneStatus {
  const changedFiles = pr.changedFiles ?? [];
  const required = laneRequired(lane, changedFiles);

  const suiteExecs = executions
    .filter(e => e.testSuiteId === lane.suiteId && e.status !== 'cancelled')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const latest = suiteExecs[0];

  if (!latest) {
    // A parked PR isn't eligible for testing, so it never shows as "needed"; a run someone started
    // manually still surfaces below.
    return required && eligible ? 'needed' : 'na';
  }
  if (latest.status === 'pending' || latest.status === 'in_progress') return 'running';

  if (lane.verify) {
    // On a verify lane a finished run is never automatically green: some diffs and error logs are
    // expected today, and whether the run is a "failure" is a human judgement over its results. So an
    // unverified run — completed OR errored — waits in 'verify' for a person to inspect it. Once a
    // human has verified it (accepting the results even if a step errored), it is treated like any
    // acceptable run below.
    if (!latest.verification) return 'verify';
  } else if (latest.status === 'failed' || (latest.failedTests ?? 0) > 0) {
    return 'failed';
  }

  // The run is acceptable to treat as green-if-fresh: a completed run on a pass/fail lane, or a
  // human-verified run on a verify lane. Decide fresh-pass vs a retest decision.
  const acceptable = lane.verify ? !!latest.verification : latest.status === 'completed';
  if (acceptable) {
    const headSha = pr.pr?.head?.sha;
    // Ran against the current head — unambiguously fresh.
    if (latest.commitHash && headSha && latest.commitHash === headSha) return 'passed';
    // A human accepted the prior result for this exact head.
    const waiver = pr.testWaivers?.[lane.suiteId];
    if (waiver && headSha && waiver.headSha === headSha) return 'passed';
    // The backend diffed the tested commit against head and nothing scoped changed since.
    const fresh = pr.testFreshness?.[lane.suiteId];
    if (
      fresh &&
      fresh.testedSha === latest.commitHash &&
      fresh.headSha === headSha &&
      !laneRequired(lane, fresh.changedFiles)
    ) {
      return 'passed';
    }
    return 'retest-decision';
  }
  return 'na';
}

const LANE_BLOCKER_TEXT: Partial<Record<LaneStatus, string>> = {
  failed: 'failing',
  verify: 'needs verification',
  running: 'running',
  needed: 'needed',
  'retest-decision': 'needs a retest decision',
};

// The unit lane's node and blocker wording per status, mirroring laneNodeForStatus/LANE_BLOCKER_TEXT
// for the scoped lanes. Absent entries (passed / na) occupy no node and block nothing.
const UNIT_NODE_FOR_STATUS: Partial<Record<UnitStatus, string>> = {
  needed: NODE.unitNeeded,
  running: NODE.unitTesting,
  failed: NODE.unitFailures,
};
const UNIT_BLOCKER_TEXT: Partial<Record<UnitStatus, string>> = {
  needed: 'Unit tests needed',
  running: 'Unit tests running',
  failed: 'Unit tests failing',
};

const PARKED_LABEL_TEXT: Record<ParkedReason, string> = {
  'upstream-wait': 'Waiting on an upstream PR (targets a non-main branch)',
  draft: 'Marked draft',
  paused: 'Paused',
  stuck: 'Stuck',
  wip: 'Work in progress',
};

// Map a scoped lane's status to the diagram node it occupies (or null when it occupies none).
function laneNodeForStatus(laneId: string, status: LaneStatus): string | null {
  const ids = laneNodeIds(laneId);
  switch (status) {
    case 'running': return ids.running;
    case 'failed': return ids.failed;
    case 'verify': return ids.verify;
    case 'needed': return ids.needed;
    case 'retest-decision': return ids.retest;
    default: return null; // passed / na
  }
}

// Derive a PR's full workflow position. Pure: depends only on the PR document and its test
// executions, so it is exhaustively unit-tested and can move to the backend unchanged later.
export function computePrWorkflow(pr: WorkflowPr, executions: TestExecution[]): PrWorkflow {
  const prNumber = pr.pr?.number ?? pr.prNumber ?? 0;
  const priority = labelNames(pr).has(PRIORITY_LABEL);
  const merged = pr.prStatus === 'merged';

  const parked = computeParked(pr);
  const eligible = parked === null;
  const review = computeReview(pr);
  const unit = computeUnit(pr);

  const lanes: Record<string, LaneStatus> = {};
  for (const lane of TEST_LANES) {
    lanes[lane.id] = computeLane(lane, pr, executions, eligible);
  }

  const laneStatuses = TEST_LANES.map(l => lanes[l.id]);
  const ready =
    !merged &&
    !parked &&
    review === 'approved' &&
    (unit === 'passed' || unit === 'na') &&
    laneStatuses.every(s => s === 'passed' || s === 'na') &&
    pr.pr?.mergeable !== false;

  const nodeIds: string[] = [];
  const blockers: string[] = [];

  if (merged) {
    nodeIds.push(NODE.merged);
  } else if (ready) {
    nodeIds.push(NODE.ready);
  } else {
    // The nodes are pushed review-position-first so that `nodeIds[0]` is the PR's headline stage
    // (the table's Stage chip reads it via primaryNodeLabel). For an approved PR there is no review
    // node, so `nodeIds[0]` becomes whatever actually blocks it — merge conflicts, a failing unit
    // run, a lane needing attention — which is exactly what we want the chip to say.

    // Review-lane position (or the parked node standing in for it). An approved PR occupies no
    // review node — approval is a green state shown by absence — so it only lands here when it is
    // still open / reviewing / changes-requested, or parked.
    if (parked) {
      nodeIds.push(parked);
      blockers.push(PARKED_LABEL_TEXT[parked]);
    } else if (review !== 'approved') {
      nodeIds.push(review);
      if (review === 'changes-requested') {
        blockers.push('Changes requested');
      } else {
        blockers.push('Not yet approved');
      }
    }

    // The diagram charts progress toward Ready, not a live inventory of every fact about a PR. So a
    // parked PR occupies only its parked node: nobody acts on a draft's or a paused PR's merge
    // conflicts or unit checks until it is unparked, and charting them would pad those stages with
    // PRs that aren't moving. They are still reported as blockers below, because the PR's own
    // Overview is exactly the place for the full inventory. (Scoped lanes are handled in computeLane:
    // a parked PR never "needs" a run, but a run someone started by hand still surfaces.)
    const charted = !parked;
    const conflicted = pr.pr?.mergeable === false;

    // Merge conflicts sit on their own path (a PR can be conflicted while still under review, or even
    // while approved and otherwise ready), so this node is independent of the review position above.
    if (conflicted && charted) nodeIds.push(NODE.mergeConflicts);

    // Unit lane. GitHub does not run pull_request workflows while a PR has merge conflicts, so a
    // conflicted PR with no check runs is not a separate unit to-do — the conflict is the one thing to
    // fix and the tests start on their own once it is. Charting it in needs-unit as well would
    // double-count the same blocker under two stages. (It stays in `blockers`, which is the inventory.)
    const unitNode = UNIT_NODE_FOR_STATUS[unit];
    const unitCharted = charted && !(unit === 'needed' && conflicted);
    if (unitNode && unitCharted) nodeIds.push(unitNode);
    const unitText = UNIT_BLOCKER_TEXT[unit];
    if (unitText) blockers.push(unitText);

    // Scoped test lanes.
    for (const lane of TEST_LANES) {
      const status = lanes[lane.id];
      const node = laneNodeForStatus(lane.id, status);
      if (node) nodeIds.push(node);
      const text = LANE_BLOCKER_TEXT[status];
      if (text) blockers.push(lane.label + ' ' + text);
    }

    if (conflicted) blockers.push('Merge conflicts');
  }

  return { prNumber, parked, review, unit, lanes, ready, merged, priority, nodeIds, blockers };
}
